import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '@/stores/app';
import { useAuthStore } from '@/stores/auth';
import { api } from '@/lib/api';
import { LoadingSpinner } from '@/components/LoadingScreen';
import { AppIcon } from '@/components/AppIcon';
import { CategoryMark } from '@/components/CategoryMark';

interface PaymentShare {
  user_id: string;
  share_percentage: number;
  share_amount: number;
}

interface Payment {
  id: string;
  description: string;
  amount: number;
  paid_at: string;
  paid_by: string;
  paid_by_name: string;
  category_id: string;
  category_name: string;
  category_icon: string;
  is_settled: boolean;
  is_instant_request: boolean;
  shares: PaymentShare[];
}

export default function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { currentYearMonth, categories, setCategories, showToast } = useAppStore();
  const [isLoading, setIsLoading] = useState(true);
  const [recentPayments, setRecentPayments] = useState<Payment[]>([]);

  // 入力フォーム状態
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    description: '',
    categoryId: '',
    paidAt: new Date().toISOString().split('T')[0],
    sharePercentage: 50,
    isInstantRequest: false,
  });

  useEffect(() => {
    loadData();
  }, [currentYearMonth]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [budgetSummaryRes, paymentsRes] = await Promise.all([
        api.getBudgetSummary(currentYearMonth),
        api.getPayments({ yearMonth: currentYearMonth }),
      ]);

      // サマリーを計算
      const summaryData = budgetSummaryRes.summary || [];
      // カテゴリ情報を抽出して保存
      const extractedCategories = summaryData.map((item: any, index: number) => ({
        id: item.categoryId,
        name: item.categoryName,
        icon: item.categoryIcon,
        color: item.categoryColor,
        sortOrder: index, // APIがソート順で返してくれるためインデックスを使用
      }));
      setCategories(extractedCategories);

      // 最新5件の未精算のみ
      setRecentPayments(
        paymentsRes.payments.filter((p: Payment) => !p.is_settled).slice(0, 5)
      );

      // カテゴリがあれば最初のカテゴリをデフォルトに
      if (extractedCategories.length > 0 && !formData.categoryId) {
        setFormData((prev) => ({
          ...prev,
          categoryId: extractedCategories[0].id,
        }));
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (instant: boolean = false) => {
    if (!formData.amount || !formData.description || !formData.categoryId) {
      showToast('金額、内容、カテゴリを入力してください', 'error');
      return;
    }

    setIsSaving(true);
    try {
      await api.createPayment({
        amount: parseInt(formData.amount),
        description: formData.description,
        categoryId: formData.categoryId,
        paidAt: formData.paidAt,
        sharePercentage: formData.sharePercentage,
        isInstantRequest: false,
        isImmediateSettlement: instant,
      });

      showToast(instant ? '清算済みとして登録しました' : '登録しました', 'success');

      // フォームをリセット
      setFormData({
        amount: '',
        description: '',
        categoryId: categories[0]?.id || '',
        paidAt: new Date().toISOString().split('T')[0],
        sharePercentage: 50,
        isInstantRequest: false,
      });

      // データを再読み込み
      loadData();
    } catch (error: any) {
      showToast(error.message || '登録に失敗しました', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `¥${amount.toLocaleString()}`;
  };

  const partnerShare = formData.amount
    ? Math.round(parseInt(formData.amount) * (100 - formData.sharePercentage) / 100)
    : 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="page-container dashboard-page">
      {/* 支払い入力カード */}
      <div className="card dashboard-form-card p-5">
        <div className="section-heading mb-5">
          <div className="section-icon section-icon-blue" aria-hidden="true">
            <AppIcon name="receipt" size={20} />
          </div>
          <div>
            <h2>支払いを記録</h2>
            <p>入力は数秒。あとで家族と共有されます。</p>
          </div>
        </div>

        {/* 金額 */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">金額</label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl text-gray-500">¥</span>
            <input
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              placeholder="0"
              className="input pl-10 text-2xl font-bold h-14"
              inputMode="numeric"
            />
          </div>
        </div>

        {/* 内容 */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">内容</label>
          <input
            type="text"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="スーパーで買い物"
            className="input"
            maxLength={100}
          />
        </div>

        {/* カテゴリ・日付 */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">カテゴリ</label>
            <select
              value={formData.categoryId}
              onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
              className="input"
            >
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">日付</label>
            <input
              type="date"
              value={formData.paidAt}
              onChange={(e) => setFormData({ ...formData, paidAt: e.target.value })}
              className="input"
            />
          </div>
        </div>

        {/* 負担割合スライダー */}
        <div className="share-split-card mb-4 rounded-2xl p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            相手の負担割合
          </label>
          <div className="px-1">
            <input
              type="range"
              min="0"
              max="100"
              value={100 - formData.sharePercentage}
              onChange={(e) =>
                setFormData({ ...formData, sharePercentage: 100 - parseInt(e.target.value) })
              }
              className="slider w-full"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>0%</span>
              <span className="text-lg font-bold text-primary-600">
                {100 - formData.sharePercentage}%
              </span>
              <span>100%</span>
            </div>
          </div>
          {formData.amount && (
            <p className="text-center text-sm text-gray-600 mt-2">
              相手負担: <span className="font-bold">{formatCurrency(partnerShare)}</span>
            </p>
          )}
          {/* プリセットボタン */}
          <div className="flex justify-center gap-2 mt-2">
            {[0, 25, 50, 75, 100].map((preset) => (
              <button
                key={preset}
                onClick={() => setFormData({ ...formData, sharePercentage: 100 - preset })}
                className={`px-2 py-1 text-xs rounded ${100 - formData.sharePercentage === preset
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-200 text-gray-700'
                  }`}
              >
                {preset}%
              </button>
            ))}
          </div>
        </div>

        {/* 登録ボタン */}
        <button
          onClick={() => handleSubmit(false)}
          disabled={isSaving}
          className="btn btn-primary btn-lg w-full mb-2"
        >
          {isSaving ? <LoadingSpinner size="sm" /> : <><AppIcon name="receipt" size={18} />登録する</>}
        </button>
        <button
          onClick={() => handleSubmit(true)}
          disabled={isSaving}
          className="btn btn-secondary btn-md w-full"
        >
          <AppIcon name="check" size={18} />
          精算済みとして登録
        </button>
      </div>

      {/* 最近の支払い（未精算） */}
      <div className="card recent-payments-card">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <div className="section-heading">
            <div className="section-icon section-icon-amber" aria-hidden="true">
              <AppIcon name="history" size={19} />
            </div>
            <div>
              <h2>最近の支払い</h2>
              <p>未精算の支払いを表示中</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/payments')}
            className="text-primary-500 text-sm font-semibold"
          >
            すべて見る →
          </button>
        </div>
        {recentPayments.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <div className="empty-icon mx-auto mb-3"><AppIcon name="spark" size={22} /></div>
            <p>未精算の支払いはありません</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {recentPayments.map((payment) => {
              const isMyPayment = payment.paid_by === user?.id;
              const myShare = payment.shares?.find(s => s.user_id === user?.id);
              const myPercentage = myShare?.share_percentage ?? 50;
              return (
                <div
                  key={payment.id}
                  className="list-item"
                  onClick={() => isMyPayment && navigate(`/payments/${payment.id}/edit`)}
                >
                  <div className="flex items-center gap-3 flex-1">
                    <CategoryMark name={payment.category_name} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{payment.description}</p>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span className={`badge ${isMyPayment ? 'badge-primary' : 'badge-secondary'}`}>
                          {isMyPayment ? 'わたし' : 'パートナー'}
                        </span>
                        {!!payment.is_instant_request && (
                          <span className="badge badge-warning">即時精算</span>
                        )}
                        {!!payment.is_settled && (
                          <span className="badge badge-success">精算済み</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">{formatCurrency(payment.amount)}</p>
                    <p className="text-xs text-gray-500">
                      {myPercentage}% 負担
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
